import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import Modal from '../../shared/Modal'
import Select from '../../ui/Select/Select'
import EmailsTagInput from '../../ui/EmailsTagInput/EmailsTagInput'
import Button from '../../ui/Button'
import { showToast } from '../../shared/Toast'
import useAssignableUsers from '../hooks/useAssignableUsers'
import IssuesService from '../../../services/api/IssuesService'
import ModalHeader from '../shared/ModalHeader'
import groupUsersByRole from '../shared/groupUsersByRole'
import {
  MODAL_BODY_CLASS, MODAL_FIELD_CLASS, MODAL_LABEL_CLASS,
  MODAL_FOOTER_END_CLASS, MODAL_CANCEL_BTN_CLASS
} from '../shared/styles'
import { TOAST_CONTAINER_ID } from './constants'

const TARGET_IDS_KEY = 'target_ids'
const EXTERNAL_EMAILS_KEY = 'external_emails'

const ERROR_MSG = 'Une erreur est survenue. Veuillez réessayer ou contacter le support '
  + 'si le problème persiste.'
const SUCCESS_MSG = 'Alerte partagée avec succès.'

const TEXTAREA_CLASS = 'form-control tw:w-full tw:rounded! tw:resize-none!'

const ShareIssueModal = ({ collectId, issueId = null, isOpen, onClose, onShared = null }) => {
  const userOptions = useAssignableUsers(collectId, isOpen, TOAST_CONTAINER_ID)
  const groupedUserOptions = useMemo(() => groupUsersByRole(userOptions), [userOptions])
  const [selectedTargets, setSelectedTargets] = useState([])
  const [externalEmails, setExternalEmails] = useState([])
  const [description, setDescription] = useState('')
  const [sharing, setSharing] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      setSelectedTargets([])
      setExternalEmails([])
      setDescription('')
      setSharing(false)
    }
  }, [isOpen])

  const handleTargetsChange = useCallback(value => setSelectedTargets(value || []), [])
  const handleDescriptionChange = useCallback(event => setDescription(event.target.value), [])

  const handleShare = useCallback(async() => {
    if (!issueId || sharing)
      return
    setSharing(true)
    try {
      const payload = {
        [TARGET_IDS_KEY]: selectedTargets.map(option => option.value),
        [EXTERNAL_EMAILS_KEY]: externalEmails,
        description
      }
      await IssuesService.shareIssue(collectId, issueId, payload)
      showToast(SUCCESS_MSG, 'success', TOAST_CONTAINER_ID)
      if (onShared)
        onShared()
      onClose()
    } catch (err) {
      const message = err?.message || ERROR_MSG
      showToast(message, 'error', TOAST_CONTAINER_ID)
    } finally {
      setSharing(false)
    }
  }, [collectId, issueId, sharing, selectedTargets, externalEmails, description, onShared, onClose])

  const handleClose = useCallback(() => {
    if (sharing)
      return
    onClose()
  }, [sharing, onClose])

  const canShare = (selectedTargets.length > 0 || externalEmails.length > 0) && !sharing

  const renderHeader = () =>
    <ModalHeader title="Partager l’alerte" onClose={handleClose} />

  const renderFooter = () => (
    <div className={MODAL_FOOTER_END_CLASS}>
      <Button
        className={MODAL_CANCEL_BTN_CLASS}
        disabled={sharing}
        variant="neutral"
        onClick={handleClose}
      >
        Annuler
      </Button>
      <Button disabled={!canShare} variant="primary" onClick={handleShare}>
        {sharing ? 'Partage en cours...' : 'Partager'}
      </Button>
    </div>
  )

  return (
    <Modal
      footer={renderFooter()}
      header={renderHeader()}
      isOpen={isOpen}
      title="Partager l’alerte"
      onClose={handleClose}
    >
      <div className={MODAL_BODY_CLASS}>
        <div className={MODAL_FIELD_CLASS}>
          <label className={MODAL_LABEL_CLASS} htmlFor="share-targets">Destinataires</label>
          <Select
            closeMenuOnSelect={false}
            menuPortalTarget={document.body}
            options={groupedUserOptions}
            placeholder="Sélectionner des utilisateurs..."
            value={selectedTargets}
            isMulti
            onChange={handleTargetsChange}
          />
        </div>
        <div className={MODAL_FIELD_CLASS}>
          <label className={MODAL_LABEL_CLASS} htmlFor="share-external-email">Emails externes</label>
          <EmailsTagInput
            emails={externalEmails}
            menuPortalTarget={document.body}
            onChange={setExternalEmails}
          />
        </div>
        <div className={MODAL_FIELD_CLASS}>
          <label className={MODAL_LABEL_CLASS} htmlFor="share-description">Description</label>
          <textarea
            className={TEXTAREA_CLASS}
            id="share-description"
            rows={6}
            value={description}
            onChange={handleDescriptionChange}
          />
        </div>
      </div>
    </Modal>
  )
}

ShareIssueModal.propTypes = {
  collectId: PropTypes.number.isRequired,
  isOpen: PropTypes.bool.isRequired,
  issueId: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onShared: PropTypes.func
}

ShareIssueModal.defaultProps = {
  issueId: null,
  onShared: null
}

export default ShareIssueModal
